import { useMemo, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import type { PublicShowcase } from '../lib/api';
import { translateFields } from '../lib/api';
import { useTranslatedFields } from '../lib/i18n';
import { pickStockByPadrao } from '../assets/builder-illustrations';

/**
 * Opening scene. Deliberately held minimal — no background photo. The
 * front and back photos get their own pinned moments later in the
 * scroll. Here we give the page a quiet anchor: the project name, the
 * one line that says what the house is, and the location.
 */
export function Hero({ data }: { data: PublicShowcase }) {
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const textOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '-10%']);

  const sourceSubheadline =
    data.heroSubheadline ??
    'Construída com a disciplina de um produto de software e o rigor de uma obra que dura.';

  // Wave 308 · LLM-translate the long-form Hero copy when locale flips
  // to EN. Eyebrow + headline are short and worth caching too. Static
  // labels (the "role para continuar" hint) stay in t().
  const fields = useMemo(
    () => ({
      'hero.eyebrow': data.heroEyebrow ?? '',
      'hero.headline': data.heroHeadline ?? '',
      'hero.subheadline': sourceSubheadline,
    }),
    [data.heroEyebrow, data.heroHeadline, sourceSubheadline],
  );
  const tx = useTranslatedFields(data.slug, fields, translateFields);
  const eyebrow = tx['hero.eyebrow'] || data.heroEyebrow || '';
  const subheadline = tx['hero.subheadline'] || sourceSubheadline;

  const heroBg = data.coverPhotoUrl ?? pickStockByPadrao(null, null, data.slug).src;

  return (
    <section
      ref={heroRef}
      className="relative h-[100vh] w-full overflow-hidden"
      aria-label="Apresentação"
    >
      {/* Wave 80 — fachada stock-arch (ou coverPhotoUrl real) como fundo
          atmosférico do hero. Opacidade baixa pra não competir com o texto. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.18,
        }}
      />
      {/* Ambient warm glow — keeps the opening breathing without
          competing with the photos that follow. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 70%, rgba(212,184,150,0.10), transparent 70%)',
        }}
      />
      <div aria-hidden className="absolute inset-0 grain pointer-events-none" />

      <motion.div
        className="relative z-10 flex min-h-screen flex-col items-start justify-end px-6 pb-20 pt-24 sm:px-12 md:pb-28 md:pt-32 lg:px-20 lg:pb-32"
        style={{ opacity: textOpacity, y: textY }}
      >
        {eyebrow && (
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="text-[11px] font-medium uppercase tracking-[0.25em] text-accent"
          >
            {eyebrow}
          </motion.p>
        )}

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-[28ch] font-display text-[clamp(1.75rem,4.2vw,3rem)] font-light leading-[1.2] tracking-[-0.015em] text-ink [text-wrap:balance]"
        >
          {subheadline}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.85 }}
          className="mt-12 text-[11px] font-medium uppercase tracking-[0.2em] text-ink-subtle"
        >
          {data.neighborhood ?? ''}
          {data.neighborhood && data.city ? ' · ' : ''}
          {data.city ? `${data.city}${data.state ? ` / ${data.state}` : ''}` : ''}
        </motion.p>
      </motion.div>

      <motion.div
        className="pointer-events-none absolute inset-x-0 bottom-6 z-10 flex justify-center"
        style={{ opacity: textOpacity }}
      >
        <div className="flex flex-col items-center gap-2 text-ink-subtle">
          <span className="text-[10px] uppercase tracking-[0.25em]">
            Role para continuar
          </span>
          <motion.span
            aria-hidden
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            className="block h-3 w-px bg-ink-subtle"
          />
        </div>
      </motion.div>
    </section>
  );
}
